import { useCallback } from 'react'

const ORIGINALS_BASE_URL =
  import.meta.env.VITE_GO_API_URL || 'http://localhost:8081'

export interface OriginalCandidate {
  source: 'saldo' | 'mirage' | 'both'
  saldo_invoice_id?: string
  mirage_doc_header_id?: number
  doc_number: string
  doc_date: string
  doc_amount?: string
  partner_name?: string
  doc_kind_description?: string
}

export interface OriginalLinkResult {
  invoice_id: string
  original_invoice_id?: string
  mirage_original_doc_header_id?: number
  original_invoice_number?: string
  original_invoice_date?: string
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function originalsFetch<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const res = await fetch(`${ORIGINALS_BASE_URL}/api/v1/originals${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`Originals API ${res.status}: ${text}`)
  }
  if (res.status === 204) {
    return undefined as unknown as T
  }
  return res.json()
}

export interface LinkPayload {
  saldo_invoice_id?: string | null
  mirage_doc_header_id?: number | null
  doc_number?: string
  doc_date?: string
}

export function useOriginals(invoiceId: string | undefined) {
  const searchCandidates = useCallback(
    async (q?: string): Promise<OriginalCandidate[]> => {
      if (!invoiceId) return []
      const params = q && q.trim() !== '' ? `?q=${encodeURIComponent(q)}` : ''
      return originalsFetch<OriginalCandidate[]>(
        `/invoices/${invoiceId}/candidates${params}`
      )
    },
    [invoiceId]
  )

  const linkOriginal = useCallback(
    async (payload: LinkPayload): Promise<OriginalLinkResult> => {
      if (!invoiceId) throw new Error('invoiceId required')
      return originalsFetch<OriginalLinkResult>(
        `/invoices/${invoiceId}/link`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      )
    },
    [invoiceId]
  )

  const clearLink = useCallback(async (): Promise<OriginalLinkResult> => {
    if (!invoiceId) throw new Error('invoiceId required')
    return originalsFetch<OriginalLinkResult>(
      `/invoices/${invoiceId}/link`,
      { method: 'DELETE' }
    )
  }, [invoiceId])

  return { searchCandidates, linkOriginal, clearLink }
}
